export const CHAT_DOCUMENT_CATALOG_LIMIT = 100;
export const CHAT_DOCUMENT_LAZY_THRESHOLD = 20;
export const LAZY_DOCUMENT_CONTEXT_REPRESENTATION = 'lazy-v1';

export const privateDocumentScopeFromLocator = (pathValue, scopeHint = '') => {
  const normalizedHint = String(scopeHint == null ? '' : scopeHint).trim().toLowerCase();
  if (normalizedHint === 'corporate') return 'corporate';

  const rawPath = String(pathValue == null ? '' : pathValue).trim();
  try {
    const parsed = new URL(rawPath, 'http://localhost');
    if (String(parsed.searchParams.get('scope') || '').trim().toLowerCase() === 'corporate') {
      return 'corporate';
    }
  } catch (e) {
    if (/(?:^|[?&])scope=corporate(?:&|$)/i.test(rawPath)) return 'corporate';
  }

  // Historical private locators did not carry a scope. They belong to the
  // personal library and must keep their existing identity namespace.
  return 'personal';
};

export const isLazyCatalogDocument = (entry) => (
  !!entry
  && typeof entry === 'object'
  && String(entry.context_representation || '').trim() === LAZY_DOCUMENT_CONTEXT_REPRESENTATION
);

const isRecoverableDocument = (entry) => (
  entry
  && typeof entry === 'object'
  && !isLazyCatalogDocument(entry)
  && entry.added_during === 'added_later'
  && entry.is_out_of_context === true
  && entry.pending_reinjection !== true
  && String(entry.key || '').trim()
  && String(entry.path || '').trim()
);

const nonEmptyString = value => {
  const normalized = String(value == null ? '' : value).trim();
  return normalized || null;
};

const documentIdentityKeys = entry => {
  if (!entry || typeof entry !== 'object') return [];
  const values = [];
  const docId = nonEmptyString(entry.doc_id);
  const key = nonEmptyString(entry.key);
  const sourceId = nonEmptyString(entry.source_id);
  const path = nonEmptyString(entry.path);
  if (docId) values.push(`doc:${docId}`);
  if (key) values.push(`key:${key}`);
  if (sourceId) values.push(`source:${sourceId}`);
  if (path) values.push(`path:${entry.is_user_document ? '1' : '0'}:${path}`);
  return values;
};

const newerTimestampValue = (currentValue, incomingValue) => {
  const current = nonEmptyString(currentValue);
  const incoming = nonEmptyString(incomingValue);
  if (!current) return incoming;
  if (!incoming) return current;
  const currentTime = Date.parse(current);
  const incomingTime = Date.parse(incoming);
  if (Number.isFinite(currentTime) && Number.isFinite(incomingTime)) {
    return incomingTime > currentTime ? incoming : current;
  }
  // An established provenance timestamp is safer than an unparseable
  // replacement from an older or partial projection.
  return current;
};

/**
 * Merge a server projection without allowing durable document provenance to
 * move backwards. Operational flags may still follow the newest projection,
 * but a late-added source and its last successful injection identity are
 * historical facts and therefore cannot be erased by a sparse/stale payload.
 */
export const mergeMonotonicDocumentProvenance = (currentEntry, incomingEntry) => {
  const current = currentEntry && typeof currentEntry === 'object' ? currentEntry : {};
  const incoming = incomingEntry && typeof incomingEntry === 'object' ? incomingEntry : {};
  const currentInjectedAt = nonEmptyString(current.last_injected_at);
  const incomingInjectedAt = nonEmptyString(incoming.last_injected_at);
  const currentInjectionId = nonEmptyString(current.injection_message_id);
  const incomingInjectionId = nonEmptyString(incoming.injection_message_id);

  let injectionMessageId = currentInjectionId || incomingInjectionId;
  if (incomingInjectionId && !currentInjectionId) {
    injectionMessageId = incomingInjectionId;
  } else if (incomingInjectionId && currentInjectionId && incomingInjectionId !== currentInjectionId) {
    const currentTime = currentInjectedAt ? Date.parse(currentInjectedAt) : NaN;
    const incomingTime = incomingInjectedAt ? Date.parse(incomingInjectedAt) : NaN;
    if (Number.isFinite(incomingTime) && (!Number.isFinite(currentTime) || incomingTime > currentTime)) {
      injectionMessageId = incomingInjectionId;
    }
  }

  const contextRepresentation = (
    isLazyCatalogDocument(current) || isLazyCatalogDocument(incoming)
  ) ? LAZY_DOCUMENT_CONTEXT_REPRESENTATION : null;
  const merged = {
    ...current,
    ...incoming,
    added_during: (
      current.added_during === 'added_later'
      || incoming.added_during === 'added_later'
    ) ? 'added_later' : 'initial',
    injection_message_id: injectionMessageId,
    last_injected_at: newerTimestampValue(currentInjectedAt, incomingInjectedAt),
    out_of_context_at: nonEmptyString(incoming.out_of_context_at)
      || nonEmptyString(current.out_of_context_at),
  };
  if (contextRepresentation) {
    merged.context_representation = contextRepresentation;
  }
  return merged;
};

const nonNegativeInteger = value => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
};

const availabilityDocumentId = entry => nonEmptyString(
  entry && (entry.doc_id || entry.source_id || entry.key)
);

const availabilityDocumentIds = entry => [...new Set([
  entry && entry.doc_id,
  entry && entry.source_id,
  entry && entry.key,
].map(nonEmptyString).filter(Boolean))];

/**
 * Normalize the additive document-availability response without trusting its
 * optional shape. Titles are resolved only from already-authorized local
 * rows, so server paths or unknown identifiers never become user-facing.
 */
export const normalizeDocumentAvailability = (rawAvailability, localDocuments = []) => {
  if (!rawAvailability || typeof rawAvailability !== 'object' || Array.isArray(rawAvailability)) {
    return null;
  }
  const localTitles = new Map();
  (Array.isArray(localDocuments) ? localDocuments : []).forEach((entry) => {
    const title = nonEmptyString(entry.page_title || entry.title);
    if (!title) return;
    availabilityDocumentIds(entry).forEach(docId => localTitles.set(docId, title));
  });

  const failed = (Array.isArray(rawAvailability.failed) ? rawAvailability.failed : [])
    .filter(entry => entry && typeof entry === 'object')
    .map((entry) => {
      const docId = availabilityDocumentId(entry);
      return {
        doc_id: docId,
        code: nonEmptyString(entry.code) || 'document_unavailable',
        retryable: entry.retryable === true,
        title: docId ? (localTitles.get(docId) || null) : null,
      };
    });

  const added = nonNegativeInteger(rawAvailability.added);
  const alreadyPresent = nonNegativeInteger(rawAvailability.already_present);
  const availableTotal = nonNegativeInteger(rawAvailability.available_total);
  if (added === null && alreadyPresent === null && availableTotal === null && !failed.length) {
    return null;
  }
  return {
    added,
    already_present: alreadyPresent,
    available_total: availableTotal,
    failed,
  };
};

/**
 * Reconcile normalized document-state arrays by all stable identities. The
 * incoming collection controls membership by default, while matching rows
 * retain monotonic provenance. Local-only mode can opt into preserving rows
 * that a lagging projection has not observed yet.
 */
export const mergeMonotonicDocumentState = (
  currentState,
  incomingState,
  { preserveCurrentOnly = false } = {},
) => {
  const current = Array.isArray(currentState) ? currentState.filter(Boolean) : [];
  const incoming = Array.isArray(incomingState) ? incomingState.filter(Boolean) : [];
  const currentByIdentity = new Map();
  current.forEach((entry) => {
    documentIdentityKeys(entry).forEach(identity => currentByIdentity.set(identity, entry));
  });

  const output = [];
  const outputByIdentity = new Map();
  const append = entry => {
    if (!entry || typeof entry !== 'object') return;
    const identities = documentIdentityKeys(entry);
    const duplicateIndex = identities
      .map(identity => outputByIdentity.get(identity))
      .find(index => Number.isInteger(index));
    if (Number.isInteger(duplicateIndex)) {
      output[duplicateIndex] = mergeMonotonicDocumentProvenance(output[duplicateIndex], entry);
      documentIdentityKeys(output[duplicateIndex]).forEach(identity => {
        outputByIdentity.set(identity, duplicateIndex);
      });
      return;
    }
    const index = output.length;
    output.push(entry);
    identities.forEach(identity => outputByIdentity.set(identity, index));
  };

  incoming.forEach((entry) => {
    const matchedCurrent = documentIdentityKeys(entry)
      .map(identity => currentByIdentity.get(identity))
      .find(Boolean);
    append(mergeMonotonicDocumentProvenance(matchedCurrent, entry));
  });

  if (preserveCurrentOnly) {
    current.forEach(append);
  }
  return output;
};

export const recoverableDocumentCount = (chatDocumentsState) => (
  (Array.isArray(chatDocumentsState) ? chatDocumentsState : [])
    .filter(isRecoverableDocument)
    .length
);

/**
 * Queue compacted documents for the next user message without changing their
 * locator or trusted source binding. Inputs are the already-normalized client
 * projections; the server remains responsible for reauthorizing each source.
 */
export const queueRecoverableDocuments = (
  chatDocumentsState,
  pendingDocumentInjections,
) => {
  const state = Array.isArray(chatDocumentsState) ? chatDocumentsState : [];
  const pending = Array.isArray(pendingDocumentInjections)
    ? pendingDocumentInjections
    : [];
  const candidates = state.filter(isRecoverableDocument);
  if (!candidates.length) {
    return {
      chatDocumentsState: state,
      pendingDocumentInjections: pending,
      queuedCount: 0,
    };
  }

  const candidateKeys = new Set(candidates.map(entry => String(entry.key)));
  const pendingByKey = new Map();
  pending.forEach((entry) => {
    if (!entry || typeof entry !== 'object') return;
    const key = String(entry.key || '').trim();
    if (key) pendingByKey.set(key, { ...entry });
  });

  candidates.forEach((entry) => {
    const key = String(entry.key);
    const existing = pendingByKey.get(key);
    pendingByKey.set(key, {
      ...(existing || {}),
      path: entry.path,
      page_title: entry.page_title || '',
      is_user_document: !!entry.is_user_document,
      force_reinject: true,
      key,
    });
  });

  return {
    chatDocumentsState: state.map((entry) => (
      entry && candidateKeys.has(String(entry.key || ''))
        ? { ...entry, pending_reinjection: true }
        : entry
    )),
    pendingDocumentInjections: Array.from(pendingByKey.values()),
    queuedCount: candidates.length,
  };
};
